import React from 'react';
import { T } from '../../../theme/tokens';
import type { GridConfig, KpiDef } from '../DataGrid';
import type { InspectionRow } from '../../../data/useParcels';
import { resultColor, Pill, LotCell, LinkText, dash, fmtDate, parseDate, calendarDaysAgo } from '../cells';
import { isoDayOf } from '../../../util/date';
import { facetText, facetPerson } from '../filterModel';
import { oxford } from '../perspectives';

const has = (s: string, ...w: string[]) => w.some(x => (s || '').toLowerCase().includes(x));
const isPass = (r: InspectionRow) => has(r.resultBucket || r.result, 'pass');
const isFail = (r: InspectionRow) => has(r.resultBucket || r.result, 'fail');
const isPartial = (r: InspectionRow) => has(r.resultBucket || r.result, 'partial');
// Action-window buckets (formula): "Overdue result" is the only genuinely-overdue one — a past
// scheduled date still awaiting a result. "Past 14 days" = recently COMPLETED (don't match 'past').
const isOverdue = (r: InspectionRow) => has(r.actionWindow, 'overdue');
const futureDate = (r: InspectionRow) => r.scheduledDate || r.calendarDate || '';
// When the row HAS a real scheduled/calendar date, the DATE decides "upcoming" — backend isNext /
// action-window flags go stale (a Jun 9 request still flagged 'next' on Jun 11 read as 4 upcoming
// inspections that weren't). Flags only rescue date-less rows the backend knows are next.
// CALENDAR-day window (calendarDaysAgo), not a rolling epoch one: the county schedules with bare
// dates, which parse to UTC midnight — the old `now − 24h` floor silently dropped ALL of today's
// scheduled visits every evening (live repro 2026-06-12 ~8pm ET: 12 inspections scheduled "today"
// vanished; the KPI read 1 when 13 were genuinely upcoming). Same class as the permits Issued-48h
// fix. (Exported for the regression harness.)
export const isNext14 = (r: InspectionRow) => {
  if (r.completedDate) return false;
  const d = calendarDaysAgo(futureDate(r));
  if (d != null) return d <= 0 && d >= -14;   // today through 14 calendar days out
  return r.isNext || has(r.actionWindow, 'next 14', 'next inspection');
};
// ISO-prefixed day keys sort chronologically via sortByKey; ±2-week operating window counted in
// CALENDAR days (same evening-boundary fix as isNext14 — the epoch window shifted day edges by hours).
// isoDayOf (not raw.slice): Completed/Scheduled render ISO but Calendar Date renders "6/12/2026" —
// slicing the raw string produced non-ISO keys that broke the chronological group order for
// upcoming (calendar-dated) inspections. (Exported for the regression harness.)
export const dayKey = (r: InspectionRow) => {
  const raw = r.completedDate || r.calendarDate || r.scheduledDate || '';
  const d = calendarDaysAgo(raw); if (d == null) return '~ no date';
  if (d < -14 || d > 14) return '~ outside ±2 wks';
  const iso = isoDayOf(raw);
  // weekday from local noon of the ISO day — naming the weekday off the raw epoch mislabels
  // ISO dates (UTC midnight = the previous local evening west of Greenwich)
  return `${iso} · ${new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}${d < 0 ? ' · upcoming' : ''}`;
};
/** A re-inspection that's still genuinely open — a later same-(permit,type) pass clears it (r.superseded). */
const openReinspect = (r: InspectionRow) => r.reinspection && !r.superseded;

/** The Inspector field is dirty — values like "Passed - Christopher Wilson" / "Partial - Miguel
 *  Flores" embed the result. Strip the result prefix so each person groups as one. */
const cleanInspector = (s: string): string => {
  const v = (s || '').trim();
  if (!v) return 'Unassigned';
  const m = v.match(/^(passed|failed|partial|cancelled|canceled|no result|complete|pending)\s*[-–:]\s*(.+)$/i);
  if (m) return m[2].trim();
  if (/^(passed|failed|partial|cancelled|canceled|no result|complete|pending|unknown)$/i.test(v)) return 'Unknown';
  return v;
};

/** Per-group analytics shown inline in each group header — pass / fail / partial %, with counts.
 *  Makes "group by Inspector" read like a scorecard: who passes vs fails. */
function groupStats(rows: InspectionRow[]): React.ReactNode {
  const n = rows.length || 1;
  const pass = rows.filter(isPass).length, fail = rows.filter(isFail).length, partial = rows.filter(isPartial).length;
  const re = rows.filter(openReinspect).length;          // open only — cleared re-inspections excluded
  const resolved = rows.filter(r => r.superseded).length; // failed/partial visits later passed for the same type
  const pct = (x: number) => Math.round((x / n) * 100);
  const chip = (label: string, color: string) => <span className="num" style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</span>;
  return <>
    {chip(`${pct(pass)}% pass (${pass})`, pct(pass) >= 80 ? T.accent : pct(pass) >= 50 ? T.gold : T.amber)}
    {fail > 0 && chip(`${pct(fail)}% fail (${fail})`, T.amber)}
    {partial > 0 && chip(`${pct(partial)}% partial (${partial})`, T.gold)}
    {re > 0 && chip(`${re} re-insp`, T.maroon)}
    {resolved > 0 && chip(`${resolved} resolved`, T.accent)}
  </>;
}

/** Overview takeaways — pure pass-rate + mix sentences computed from the scoped rows. Pass rate
 *  is honest: over RESULTED inspections only (pass+fail+partial), never over unresulted ones.
 *  Exported for the regression harness. */
export function inspectionsTakeaways(rows: InspectionRow[]): string[] {
  if (!rows.length) return [];
  const n = (x: number) => x.toLocaleString('en-US');
  const permits = new Set(rows.map(r => r.permitNumber).filter(Boolean)).size;
  const pass = rows.filter(isPass).length, fail = rows.filter(isFail).length, partial = rows.filter(isPartial).length;
  const resulted = pass + fail + partial;
  const out: string[] = [];
  out.push(resulted > 0
    ? `${n(rows.length)} inspection${rows.length === 1 ? '' : 's'} across ${n(permits)} permit${permits === 1 ? '' : 's'} — ${Math.round((pass / resulted) * 100)}% pass rate (${n(pass)} passed, ${n(fail)} failed, ${n(partial)} partial).`
    : `${n(rows.length)} inspection${rows.length === 1 ? '' : 's'} across ${n(permits)} permit${permits === 1 ? '' : 's'} — none have a logged result yet.`);
  const resolved = rows.filter(r => r.superseded).length;
  const re = rows.filter(openReinspect).length;
  const od = rows.filter(isOverdue).length;
  const up = rows.filter(isNext14).length;
  out.push(`${n(resolved)} earlier failed/partial visit${resolved === 1 ? ' was' : 's were'} cleared by a later pass; ${n(re)} re-inspection${re === 1 ? '' : 's'} and ${n(od)} overdue result${od === 1 ? '' : 's'} remain open, with ${n(up)} visit${up === 1 ? '' : 's'} coming in the next two weeks.`);
  const byType = new Map<string, number>();
  for (const r of rows) { const t = (r.type || '').trim(); if (t && !/^unknown$/i.test(t)) byType.set(t, (byType.get(t) || 0) + 1); }
  const top = [...byType.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} ${n(v)}`);
  if (top.length) out.push(`Busiest types: ${oxford(top)}.`);
  return out;
}

export function makeInspectionsConfig(): GridConfig<InspectionRow> {
  const kpis: KpiDef<InspectionRow>[] = [
    { label: 'Inspections', define: 'All inspections for the lots in view, inside the active time window and search.',
      compute: rows => ({ display: '', n: rows.length }) },
    { label: 'Passed', cluster: 'Results', define: 'Inspections whose result bucket reads Passed.',
      compute: rows => ({ display: '', n: rows.filter(isPass).length, tone: 'accent' }), filter: isPass },
    { label: 'Failed', cluster: 'Results', define: 'Failed inspections still open — a later pass of the same permit + type clears them into Resolved.',
      compute: rows => ({ display: '', n: rows.filter(r => isFail(r) && !r.superseded).length, tone: 'amber' }), filter: r => isFail(r) && !r.superseded },
    { label: 'Resolved', cluster: 'Results', define: 'Past failed or partial visits superseded by a later pass of the same permit + inspection type.',
      compute: rows => ({ display: '', n: rows.filter(r => r.superseded).length, tone: 'accent' }), filter: r => r.superseded },
    { label: 'Overdue', cluster: 'Attention', axis: 'attention', define: 'Past the scheduled date with no result logged yet.',
      compute: rows => ({ display: '', n: rows.filter(isOverdue).length, tone: 'amber' }), filter: isOverdue },
    { label: 'Reinspect', cluster: 'Attention', axis: 'attention', define: 'Open re-inspections — a follow-up visit not yet cleared by a pass.',
      compute: rows => ({ display: '', n: rows.filter(openReinspect).length, tone: 'maroon' }), filter: openReinspect },
    { label: 'Next ≤14d', cluster: 'Attention', axis: 'attention', define: 'Uncompleted inspections dated today through 14 days out (or flagged next by the county when undated).',
      compute: rows => ({ display: '', n: rows.filter(isNext14).length }), filter: isNext14 },
  ];

  const columns: GridConfig<InspectionRow>['columns'] = [
    { key: 'type', header: 'Type · Phase', width: 200, sortable: true, section: 'Identity', value: r => r.type,
      render: r => {
        // "Unknown" over an "UNKNOWN" eyebrow is a stutter — collapse to one muted Unknown.
        const unk = (s: string | null | undefined) => /^unknown$/i.test((s || '').trim());
        if (unk(r.type) && unk(r.phase)) return <span style={{ color: T.inkSoft }}>Unknown</span>;
        return <div style={{ minWidth: 0 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dash(r.type)}</div>
          {r.phase ? <span className="eyebrow" style={{ color: T.inkSoft }}>{r.phase}</span> : null}</div>;
      } },
    { key: 'result', header: 'Result', width: 120, sortable: true, section: 'Status', value: r => r.result,
      render: r => <span title={r.result || undefined}><Pill label={r.result} color={resultColor(r.resultBucket, r.result)} /></span> },
    // value = the displayed status text (exports/search read it); sortValue = Operational Rank (§6)
    { key: 'op', header: 'Status', width: 170, sortable: true, section: 'Status', value: r => r.opStatus,
      sortValue: r => r.opRank,
      render: r => r.opStatus ? <span title={r.opStatus}><Pill label={r.opStatus} color={T.inkSoft} /></span> : <span style={{ color: T.inkSoft }}>—</span> },
    { key: 'permit', header: 'Permit #', width: 120, sortable: true, section: 'Identity', value: r => r.permitNumber,
      render: (r, ctx) => <LinkText label={dash(r.permitNumber)} url={r.permitUrl} ctx={ctx} mono /> },
    { key: 'lot', header: 'Lot', width: 168, sortable: true, section: 'Identity', value: r => r.lotName || r.blockLot,
      render: (r, ctx) => <LotCell blockLot={r.blockLot} lotId={r.lotId} name={r.lotName} extra={Math.max(0, r.lotIds.length - 1)} ctx={ctx} /> },
    // sortValue = epoch: the raw strings mix ISO and US formats, so a lexicographic sort on
    // `value` ordered "10/1/2026" before "9/1/2026". value stays the raw string for CSV/PDF.
    { key: 'date', header: 'Date', width: 120, align: 'right', sortable: true, defaultDesc: true, section: 'Activity', value: r => r.calendarDate || r.scheduledDate || r.completedDate,
      sortValue: r => parseDate(r.calendarDate || r.scheduledDate || r.completedDate) || null,
      render: r => <span className="num" title={(r.calendarDate || r.scheduledDate || r.completedDate || '').slice(0, 10)}>{fmtDate(r.calendarDate || r.scheduledDate || r.completedDate)}</span> },
    { key: 'inspector', header: 'Inspector', width: 180, sortable: true, section: 'Status', value: r => cleanInspector(r.inspector), render: r => { const v = cleanInspector(r.inspector); return <span style={/unknown|unassigned/i.test(v) ? { color: T.inkSoft } : undefined}>{/unknown/i.test(v) ? '—' : v}</span>; } },
    // `value` = the label string (what CSV/PDF export and sort see), not a flag count — and ''
    // when no flags, so hideWhenEmpty can still drop an all-blank column.
    // 'next' here mirrors isNext14 (date-honest), not the raw backend flag — the KPI and the
    // flag column must agree or stale rows read as upcoming in one place and not the other.
    { key: 'flags', header: 'Flags', width: 120, align: 'right', hideWhenEmpty: true,
      value: r => r.superseded ? 'resolved'
        : [r.isLatest ? 'latest' : '', isNext14(r) ? 'next' : '', r.reinspection ? 're-insp' : ''].filter(Boolean).join(' · '),
      render: r => r.superseded
        ? <span className="num" style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>✓ resolved</span>
        : <span className="num" style={{ fontSize: 11, color: T.inkSoft }}>
          {[r.isLatest ? 'latest' : '', isNext14(r) ? 'next' : '', r.reinspection ? 're-insp' : ''].filter(Boolean).join(' · ') || '—'}</span> },
  ];

  return {
    columns, kpis,
    // Styled-export hints: the inspection date as a REAL Excel date; result cells toned like
    // the pill (pass = green, fail = rose, partial = amber).
    excel: {
      sheetTitle: 'Inspections',
      date: ['date'],
      tone: {
        result: r => isPass(r) ? 'good' : isFail(r) ? 'blocked' : isPartial(r) ? 'progress'
          : (r.result || r.resultBucket) ? 'neutral' : undefined,
      },
      pdfColumns: ['type', 'result', 'permit', 'lot', 'date', 'inspector', 'flags'],
    },
    groupKey: r => r.actionWindow || '—',
    dateOf: r => r.completedDate || r.calendarDate || r.scheduledDate,
    groupOrder: [],
    groupOptions: [
      { key: 'window', label: 'Action window', keyOf: r => r.actionWindow || '—' },
      { key: 'day', label: 'Day (±2 wks)', keyOf: dayKey, sortByKey: true },
      { key: 'inspector', label: 'Inspector', keyOf: r => cleanInspector(r.inspector), col: 'inspector' },
      { key: 'result', label: 'Result', keyOf: r => r.resultBucket || r.result || 'No result', col: 'result' },
      { key: 'type', label: 'Inspection type', keyOf: r => r.type || '—' },
      { key: 'phase', label: 'Phase', keyOf: r => r.phase || '—' },
      { key: 'lot', label: 'Lot', keyOf: r => r.lotName ? `${r.lotName} · ${r.blockLot}` : (r.blockLot || '—'), col: 'lot' },
    ],
    groupStats,
    defaultSort: { key: 'date', dir: 'desc' },  // latest inspections first within each bucket
    rowLotId: r => r.lotId, rowKey: r => r.id,
    ariaLabel: r => `Inspection ${r.type || ''}, permit ${r.permitNumber || '—'}, lot ${r.blockLot || '—'}, ${r.result || 'no result'}, inspector ${r.inspector || 'unknown'}`,
    // field:value facets — inspector accepts inspector:unassigned (cleanInspector maps blanks).
    facets: {
      type: facetText(r => r.type),
      phase: facetText(r => r.phase),
      result: facetText(r => r.resultBucket || r.result),
      inspector: facetPerson(r => { const v = cleanInspector(r.inspector); return /unassigned|unknown/i.test(v) ? '' : v; }),
      permit: facetText(r => r.permitNumber),
      lot: facetText(r => `${r.lotName} ${r.blockLot}`),
    },
    presetViews: [
      { name: 'Upcoming 2 weeks', preset: true, chips: ['Next ≤14d'], groupBy: 'day', sort: { key: 'date', dir: 'asc' } },
      { name: 'Open failures', preset: true, chips: ['Failed'], sort: { key: 'date', dir: 'desc' } },
      { name: 'Inspector scorecard', preset: true, groupBy: 'inspector' },
    ],
    // Curated sub-views (pill row under the tab bar; "All" = this full config).
    perspectives: [
      { key: 'upcoming', label: 'Upcoming & requests',
        blurb: 'County visits scheduled or requested for the next two weeks, soonest first.',
        kpiKeys: ['Inspections', 'Next ≤14d', 'Overdue'],
        columnKeys: ['type', 'permit', 'lot', 'date', 'op', 'inspector', 'flags'],
        defaults: { sortKey: 'date', sortDir: 'asc', chips: ['Next ≤14d'], groupBy: 'day' } },
      { key: 'issues', label: 'Failures & issues',
        blurb: 'Open failures, re-inspections, and overdue results to chase down.',
        kpiKeys: ['Failed', 'Reinspect', 'Overdue', 'Resolved'],
        columnKeys: ['type', 'result', 'permit', 'lot', 'date', 'inspector', 'flags'],
        defaults: { sortKey: 'date', sortDir: 'desc' } },
      { key: 'overview', label: 'Overview',
        blurb: 'Pass rates and inspection mix across the whole selection.',
        kpiKeys: ['Inspections', 'Passed', 'Failed', 'Resolved'],
        columnKeys: ['type', 'result', 'permit', 'lot', 'date', 'inspector'],
        defaults: { groupBy: 'type' },
        takeaways: inspectionsTakeaways },
    ],
    empty: 'No inspections for this selection.',
    nextWindowEmpty: 'Nothing scheduled ahead — county inspection requests usually post ~1 day before the visit (refreshed by the morning sync).',
  };
}
